type FaroEnvironment = Record<string, string | undefined>;

enum FaroDeploymentEnvironment {
    Development = 'development',
    Uat = 'uat',
    Production = 'production',
}

const FARO_DEFAULT_ENVIRONMENT = FaroDeploymentEnvironment.Development;
const FARO_HOSTED_DEV_ORIGIN = 'https://dev.crm.activix.ca';
const FARO_HOSTED_UAT_ORIGIN = 'https://uat.crm.activix.ca';
const FARO_HOSTED_PRODUCTION_ORIGIN = 'https://crm.activix.ca';

type FaroDeployment = {
    environment: FaroDeploymentEnvironment;
    origin: string;
    collectorUrl: string;
    approvedRequestUrls: RegExp[];
    ignoredRequestUrls: RegExp[];
};

const buildDeployment = (
    environment: FaroDeploymentEnvironment,
    origin: string,
    approvedRequestUrls: RegExp[]
): Omit<FaroDeployment, 'collectorUrl'> => ({
    environment,
    origin,
    approvedRequestUrls,
    ignoredRequestUrls: [
        new RegExp(`^(?!${approvedRequestUrls.map(({ source }) => source).join('|')}).+`, 'i'),
    ],
});

const DEPLOYMENTS = new Map<string, Omit<FaroDeployment, 'collectorUrl'>>([
    [
        FaroDeploymentEnvironment.Development,
        buildDeployment(FaroDeploymentEnvironment.Development, FARO_HOSTED_DEV_ORIGIN, [
            // Preserve the existing DEV /api and /oauth proxy destinations.
            /^https:\/\/dev\.crm\.activix\.ca(?::443)?\/(?:api|oauth)(?:\/|$)/i,
            // Preserve the existing DEV direct API destination.
            /^https:\/\/api\.dev\.crm\.activix\.ca(?::443)?(?:\/|$)/i,
        ]),
    ],
    [
        FaroDeploymentEnvironment.Uat,
        buildDeployment(FaroDeploymentEnvironment.Uat, FARO_HOSTED_UAT_ORIGIN, [
            // Match the deployed UAT client's same-origin API base URL.
            /^https:\/\/uat\.crm\.activix\.ca(?::443)?\/api(?:\/|$)/i,
        ]),
    ],
    [
        FaroDeploymentEnvironment.Production,
        buildDeployment(FaroDeploymentEnvironment.Production, FARO_HOSTED_PRODUCTION_ORIGIN, [
            // Match the deployed production client's direct API base URL.
            /^https:\/\/api\.crm\.activix\.ca(?::443)?(?:\/|$)/i,
        ]),
    ],
]);

// Vite replaces individual VUE_APP_* expressions, not the process.env object.
const getFaroEnvironment = (): FaroEnvironment => ({
    VUE_APP_ENV: process.env.VUE_APP_ENV || FARO_DEFAULT_ENVIRONMENT,
    VUE_APP_FARO_COLLECTOR_URL: process.env.VUE_APP_FARO_COLLECTOR_URL,
    VUE_APP_VERSION: process.env.VUE_APP_VERSION,
});

const getCollectorUrl = (env: FaroEnvironment): string | null => {
    const collectorUrl = env.VUE_APP_FARO_COLLECTOR_URL?.trim();
    if (!collectorUrl) {
        return null;
    }
    try {
        const parsedUrl = new URL(collectorUrl);
        return parsedUrl.protocol === 'https:' && !parsedUrl.username && !parsedUrl.password
            ? parsedUrl.toString()
            : null;
    } catch {
        return null;
    }
};

const resolveFaroDeployment = (
    env: FaroEnvironment = getFaroEnvironment(),
    origin: string = globalThis.location.origin
): FaroDeployment | null => {
    const collectorUrl = getCollectorUrl(env);
    const deployment = DEPLOYMENTS.get(env.VUE_APP_ENV ?? '');

    if (origin !== deployment?.origin || !collectorUrl) {
        return null;
    }

    return { ...deployment, collectorUrl };
};

const isFaroEnabled = (
    env: FaroEnvironment = getFaroEnvironment(),
    origin: string = globalThis.location.origin
): boolean => resolveFaroDeployment(env, origin) !== null;

export {
    FaroDeploymentEnvironment,
    FARO_DEFAULT_ENVIRONMENT,
    FARO_HOSTED_DEV_ORIGIN,
    getCollectorUrl,
    getFaroEnvironment,
    isFaroEnabled,
    resolveFaroDeployment,
    type FaroDeployment,
    type FaroEnvironment,
};
